import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, Users, BookOpen, Activity, FileText, Download, RefreshCw,
  TrendingUp, TrendingDown, Calendar, Target, MessageSquare, GraduationCap,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { AdminApi } from '../../services/api';
import './AdminAnalyticsPanel.css';

const PERIOD_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '12m': 'Last 12 months',
  custom: 'Custom range',
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatRange = (start, end) => {
  if (!start || !end) return '';
  return `${formatShortDate(start)} – ${formatShortDate(end)}`;
};

const DeltaBadge = ({ change }) => {
  if (change == null) return null;
  const cls = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const Icon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : null;
  return (
    <span className={`analytics-kpi__delta analytics-kpi__delta--${cls}`}>
      {Icon && <Icon size={12} />}
      {change > 0 ? '+' : ''}{change}% vs prior
    </span>
  );
};

const HorizontalBars = ({ items, max, colorClass = '' }) => {
  const peak = max || Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="analytics-bar-list">
      {items.map((item) => (
        <div key={item.label} className="analytics-bar-row">
          <span className="analytics-bar-row__label" title={item.label}>{item.label}</span>
          <div className="analytics-bar-row__track">
            <div
              className={`analytics-bar-row__fill ${colorClass}`}
              style={{ width: `${(item.count / peak) * 100}%` }}
            />
          </div>
          <span className="analytics-bar-row__count">{item.count}</span>
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ label, value, sub, comparison, compareKey }) => {
  const delta = comparison?.[compareKey];
  return (
    <div className="analytics-kpi">
      <span className="analytics-kpi__label">{label}</span>
      <span className="analytics-kpi__value">{value}</span>
      {sub && <span className="analytics-kpi__sub">{sub}</span>}
      {delta && <DeltaBadge change={delta.change} />}
    </div>
  );
};

const AdminAnalyticsPanel = ({ token, activeSubTab = 'overview', onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [comparison, setComparison] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { period, comparison };
      if (period === 'custom' && dateRange.start && dateRange.end) {
        params.startDate = dateRange.start;
        params.endDate = dateRange.end;
      }
      const res = await AdminApi.getAnalytics(token, params);
      if (res.success) {
        setData(res.data);
      } else {
        onNotify?.('error', 'Analytics Failed', res.message || 'Could not load analytics.');
      }
    } catch (err) {
      onNotify?.('error', 'Analytics Failed', err.message || 'Could not load analytics.');
    } finally {
      setLoading(false);
    }
  }, [token, period, dateRange, comparison, onNotify]);

  useEffect(() => {
    if (period === 'custom' && (!dateRange.start || !dateRange.end)) return;
    fetchAnalytics();
  }, [fetchAnalytics, period, dateRange.start, dateRange.end]);

  const mergedChartData = useMemo(() => {
    if (!data?.charts) return [];
    const map = new Map();
    data.charts.userSignups?.forEach((d) => {
      map.set(d.date, { date: d.date, signups: d.count, enrollments: 0, activity: 0 });
    });
    data.charts.enrollments?.forEach((d) => {
      const row = map.get(d.date) || { date: d.date, signups: 0, enrollments: 0, activity: 0 };
      row.enrollments = d.count;
      map.set(d.date, row);
    });
    data.charts.activity?.forEach((d) => {
      const row = map.get(d.date) || { date: d.date, signups: 0, enrollments: 0, activity: 0 };
      row.activity = d.count;
      map.set(d.date, row);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const exportCsv = useCallback(async () => {
    if (!data) return;
    setExporting(true);
    try {
      const rows = [
        ['QoreAcademy Analytics Report'],
        ['Period', PERIOD_LABELS[period] || period],
        ['Range', formatRange(data.range?.start, data.range?.end)],
        ['Generated', new Date().toISOString()],
        [],
        ['Platform Totals'],
        ['Users', data.totals?.users ?? 0],
        ['Courses', data.totals?.courses ?? 0],
        ['Enrollments', data.totals?.enrollments ?? 0],
        ['Lessons', data.totals?.lessons ?? 0],
        ['Completion Rate', data.totals?.completionRate != null ? `${data.totals.completionRate}%` : '—'],
        [],
        ['Period Activity'],
        ['New Users', data.periodStats?.newUsers ?? 0],
        ['New Enrollments', data.periodStats?.newEnrollments ?? 0],
        ['Completed Enrollments', data.periodStats?.completedEnrollments ?? 0],
        ['Active Users (logged in)', data.periodStats?.activeUsers ?? 0],
        ['Courses Published', data.periodStats?.coursesPublished ?? 0],
        ['Forum Posts', data.periodStats?.forumPosts ?? 0],
        [],
        ['Daily Signups'],
        ['Date', 'Count'],
        ...(data.charts?.userSignups?.map((d) => [d.date, d.count]) || []),
        [],
        ['Daily Enrollments'],
        ['Date', 'Count'],
        ...(data.charts?.enrollments?.map((d) => [d.date, d.count]) || []),
        [],
        ['Top Courses'],
        ['Title', 'Category', 'Enrollments'],
        ...(data.topCourses?.map((c) => [c.title, c.category, c.enrollments]) || []),
      ];

      const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qoreacademy-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      onNotify?.('success', 'Export Complete', 'Analytics report downloaded.');
    } catch (err) {
      onNotify?.('error', 'Export Failed', err.message);
    } finally {
      setExporting(false);
    }
  }, [data, period, onNotify]);

  const sectionTitle = {
    overview: 'Platform Overview',
    users: 'User Analytics',
    content: 'Content Performance',
    engagement: 'Engagement',
    reports: 'Reports & Export',
  }[activeSubTab] || 'Analytics';

  if (loading && !data) {
    return (
      <div className="analytics-panel">
        <div className="analytics-loading">
          <RefreshCw size={28} className="spin" />
          <p>Loading analytics…</p>
        </div>
      </div>
    );
  }

  const ps = data?.periodStats || {};
  const totals = data?.totals || {};
  const breakdown = data?.breakdown || {};
  const cmp = data?.comparison;

  return (
    <div className="analytics-panel">
      <div className="analytics-panel__header">
        <div>
          <h2><BarChart3 size={22} /> {sectionTitle}</h2>
          <p>Real platform metrics from enrollments, users, courses, and activity logs</p>
          {data?.range && (
            <span className="analytics-range-badge">
              <Calendar size={12} />
              {formatRange(data.range.start, data.range.end)}
            </span>
          )}
        </div>
        <div className="analytics-panel__controls">
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
            <option value="custom">Custom range</option>
          </select>
          {period === 'custom' && (
            <>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((p) => ({ ...p, start: e.target.value }))}
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((p) => ({ ...p, end: e.target.value }))}
              />
            </>
          )}
          <label className="analytics-panel__toggle">
            <input
              type="checkbox"
              checked={comparison}
              onChange={(e) => setComparison(e.target.checked)}
            />
            Compare prior period
          </label>
          <button type="button" className="analytics-panel__btn" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            className="analytics-panel__btn analytics-panel__btn--primary"
            onClick={exportCsv}
            disabled={exporting || !data}
          >
            <Download size={14} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {!data ? (
        <div className="analytics-empty">
          <BarChart3 size={40} style={{ opacity: 0.4 }} />
          <p>No analytics data available.</p>
          <button type="button" className="analytics-panel__btn" onClick={fetchAnalytics}>Retry</button>
        </div>
      ) : (
        <>
          {/* OVERVIEW */}
          {activeSubTab === 'overview' && (
            <>
              <div className="analytics-kpi-grid">
                <KpiCard
                  label="New Users"
                  value={(ps.newUsers ?? 0).toLocaleString()}
                  sub="in selected period"
                  comparison={cmp}
                  compareKey="newUsers"
                />
                <KpiCard
                  label="New Enrollments"
                  value={(ps.newEnrollments ?? 0).toLocaleString()}
                  sub="in selected period"
                  comparison={cmp}
                  compareKey="newEnrollments"
                />
                <KpiCard
                  label="Active Users"
                  value={(ps.activeUsers ?? 0).toLocaleString()}
                  sub="logged in during period"
                  comparison={cmp}
                  compareKey="activeUsers"
                />
                <KpiCard
                  label="Completion Rate"
                  value={totals.completionRate != null ? `${totals.completionRate}%` : '—'}
                  sub={`${totals.completedEnrollments ?? 0} total completed`}
                />
                <KpiCard
                  label="Platform Users"
                  value={(totals.users ?? 0).toLocaleString()}
                  sub="all time"
                />
                <KpiCard
                  label="Total Enrollments"
                  value={(totals.enrollments ?? 0).toLocaleString()}
                  sub={`${totals.courses ?? 0} courses · ${totals.lessons ?? 0} lessons`}
                />
              </div>

              <div className="analytics-grid-2">
                <div className="analytics-card-block">
                  <h3><TrendingUp size={16} /> Growth Trend</h3>
                  {mergedChartData.length > 0 ? (
                    <div className="analytics-chart-wrap">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={mergedChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={formatShortDate}
                            stroke="#64748b"
                            fontSize={11}
                          />
                          <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                          <Tooltip
                            labelFormatter={formatShortDate}
                            contentStyle={{ background: '#0f172a', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8 }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="signups" name="Signups" stroke="#f59e0b" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="enrollments" name="Enrollments" stroke="#10b981" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="analytics-empty">No trend data for this period.</p>
                  )}
                </div>

                <div className="analytics-card-block">
                  <h3><Activity size={16} /> Activity Trend</h3>
                  {mergedChartData.length > 0 ? (
                    <div className="analytics-chart-wrap">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={mergedChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                          <XAxis dataKey="date" tickFormatter={formatShortDate} stroke="#64748b" fontSize={11} />
                          <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                          <Tooltip labelFormatter={formatShortDate} contentStyle={{ background: '#0f172a', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8 }} />
                          <Bar dataKey="activity" name="Events" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="analytics-empty">No activity logged in this period.</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* USERS */}
          {activeSubTab === 'users' && (
            <>
              <div className="analytics-kpi-grid">
                <KpiCard label="New Signups" value={(ps.newUsers ?? 0).toLocaleString()} comparison={cmp} compareKey="newUsers" />
                <KpiCard label="Active Users" value={(ps.activeUsers ?? 0).toLocaleString()} sub="logged in" comparison={cmp} compareKey="activeUsers" />
                <KpiCard label="Total Users" value={(totals.users ?? 0).toLocaleString()} sub="platform-wide" />
                <KpiCard
                  label="Forum Posts"
                  value={(ps.forumPosts ?? 0).toLocaleString()}
                  sub="in period"
                  comparison={cmp}
                  compareKey="forumPosts"
                />
              </div>

              <div className="analytics-grid-2">
                <div className="analytics-card-block">
                  <h3><Users size={16} /> Users by Role</h3>
                  <HorizontalBars
                    colorClass="analytics-bar-row__fill--violet"
                    items={(breakdown.usersByRole || []).map((r) => ({
                      label: r.role,
                      count: r.count,
                    }))}
                  />
                </div>
                <div className="analytics-card-block">
                  <h3><TrendingUp size={16} /> Daily Signups</h3>
                  {data.charts?.userSignups?.length > 0 ? (
                    <div className="analytics-chart-wrap analytics-chart-wrap--sm">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.charts.userSignups}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                          <XAxis dataKey="date" tickFormatter={formatShortDate} stroke="#64748b" fontSize={10} />
                          <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
                          <Tooltip labelFormatter={formatShortDate} contentStyle={{ background: '#0f172a', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8 }} />
                          <Bar dataKey="count" name="Signups" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="analytics-empty">No signups in this period.</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* CONTENT */}
          {activeSubTab === 'content' && (
            <>
              <div className="analytics-kpi-grid">
                <KpiCard label="New Enrollments" value={(ps.newEnrollments ?? 0).toLocaleString()} comparison={cmp} compareKey="newEnrollments" />
                <KpiCard
                  label="Completed"
                  value={(ps.completedEnrollments ?? 0).toLocaleString()}
                  sub={ps.periodCompletionRate != null ? `${ps.periodCompletionRate}% of new` : undefined}
                  comparison={cmp}
                  compareKey="completedEnrollments"
                />
                <KpiCard label="Courses Published" value={(ps.coursesPublished ?? 0).toLocaleString()} comparison={cmp} compareKey="coursesPublished" />
                <KpiCard label="New Courses" value={(ps.newCourses ?? 0).toLocaleString()} sub="created in period" />
              </div>

              <div className="analytics-grid-2">
                <div className="analytics-card-block">
                  <h3><BookOpen size={16} /> Top Courses (by enrollments)</h3>
                  {data.topCourses?.length > 0 ? (
                    <div className="analytics-table-wrap">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Course</th>
                            <th>Category</th>
                            <th>Enrollments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.topCourses.map((c, i) => (
                            <tr key={c.id || i}>
                              <td><span className="analytics-rank">{i + 1}</span></td>
                              <td>{c.title}</td>
                              <td>{c.category}</td>
                              <td><strong>{c.enrollments}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="analytics-empty">No enrollments in this period.</p>
                  )}
                </div>

                <div className="analytics-card-block">
                  <h3><GraduationCap size={16} /> Course Categories</h3>
                  <HorizontalBars
                    items={(breakdown.coursesByCategory || []).map((c) => ({
                      label: c.category,
                      count: c.count,
                    }))}
                  />
                </div>
              </div>

              <div className="analytics-grid-2">
                <div className="analytics-card-block">
                  <h3><Target size={16} /> Course Status</h3>
                  <HorizontalBars
                    colorClass="analytics-bar-row__fill--emerald"
                    items={(breakdown.coursesByStatus || []).map((s) => ({
                      label: s.status,
                      count: s.count,
                    }))}
                  />
                </div>
                <div className="analytics-card-block">
                  <h3><Activity size={16} /> Enrollment Status</h3>
                  <HorizontalBars
                    colorClass="analytics-bar-row__fill--violet"
                    items={(breakdown.enrollmentsByStatus || []).map((s) => ({
                      label: s.status,
                      count: s.count,
                    }))}
                  />
                </div>
              </div>
            </>
          )}

          {/* ENGAGEMENT */}
          {activeSubTab === 'engagement' && (
            <>
              <div className="analytics-kpi-grid">
                <KpiCard label="Total Events" value={(ps.totalActivity ?? 0).toLocaleString()} sub="activity log entries" />
                <KpiCard label="Forum Posts" value={(ps.forumPosts ?? 0).toLocaleString()} comparison={cmp} compareKey="forumPosts" />
                <KpiCard
                  label="Completion Rate"
                  value={totals.completionRate != null ? `${totals.completionRate}%` : '—'}
                  sub="all enrollments"
                />
                <KpiCard label="Active Users" value={(ps.activeUsers ?? 0).toLocaleString()} comparison={cmp} compareKey="activeUsers" />
              </div>

              <div className="analytics-grid-2">
                <div className="analytics-card-block">
                  <h3><Activity size={16} /> Activity by Category</h3>
                  {(breakdown.activityByCategory || []).length > 0 ? (
                    <HorizontalBars
                      colorClass="analytics-bar-row__fill--emerald"
                      items={breakdown.activityByCategory.map((a) => ({
                        label: a.category,
                        count: a.count,
                      }))}
                    />
                  ) : (
                    <p className="analytics-empty">No categorized activity in this period.</p>
                  )}
                </div>

                <div className="analytics-card-block">
                  <h3><MessageSquare size={16} /> Top Actions</h3>
                  {(breakdown.topActions || []).length > 0 ? (
                    <div className="analytics-table-wrap">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Action</th>
                            <th>Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.topActions.map((a, i) => (
                            <tr key={a.action || i}>
                              <td>{a.action}</td>
                              <td><strong>{a.count}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="analytics-empty">No actions logged in this period.</p>
                  )}
                </div>
              </div>

              {mergedChartData.length > 0 && (
                <div className="analytics-card-block">
                  <h3><BarChart3 size={16} /> Daily Activity Volume</h3>
                  <div className="analytics-chart-wrap analytics-chart-wrap--sm">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={mergedChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                        <XAxis dataKey="date" tickFormatter={formatShortDate} stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                        <Tooltip labelFormatter={formatShortDate} contentStyle={{ background: '#0f172a', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8 }} />
                        <Line type="monotone" dataKey="activity" name="Events" stroke="#22d3ee" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}

          {/* REPORTS */}
          {activeSubTab === 'reports' && (
            <div className="analytics-reports-grid">
              <div className="analytics-report-card">
                <h4><FileText size={18} /> Full Analytics Export</h4>
                <p>
                  Download a CSV with platform totals, period stats, daily signups,
                  enrollments, and top courses for {PERIOD_LABELS[period]?.toLowerCase() || period}.
                </p>
                <button
                  type="button"
                  className="analytics-panel__btn analytics-panel__btn--primary"
                  onClick={exportCsv}
                  disabled={exporting}
                >
                  <Download size={14} />
                  {exporting ? 'Generating…' : 'Download CSV Report'}
                </button>
              </div>

              <div className="analytics-report-card">
                <h4><Users size={18} /> User Summary</h4>
                <p>Platform has {(totals.users ?? 0).toLocaleString()} users with {(ps.newUsers ?? 0).toLocaleString()} new signups in the selected window.</p>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.84rem', color: 'var(--admin-text-muted)' }}>
                  {(breakdown.usersByRole || []).map((r) => (
                    <li key={r.role}>{r.role}: {r.count}</li>
                  ))}
                </ul>
              </div>

              <div className="analytics-report-card">
                <h4><BookOpen size={18} /> Content Summary</h4>
                <p>
                  {(totals.courses ?? 0).toLocaleString()} courses, {(totals.lessons ?? 0).toLocaleString()} lessons,
                  and {(totals.enrollments ?? 0).toLocaleString()} total enrollments.
                  {totals.completionRate != null && ` Overall completion: ${totals.completionRate}%.`}
                </p>
              </div>

              <div className="analytics-report-card">
                <h4><Activity size={18} /> Engagement Summary</h4>
                <p>
                  {(ps.totalActivity ?? 0).toLocaleString()} logged events and {(ps.forumPosts ?? 0).toLocaleString()} forum posts
                  during the selected period.
                  {cmp?.activeUsers && (
                    <> Active users {cmp.activeUsers.change > 0 ? 'up' : cmp.activeUsers.change < 0 ? 'down' : 'flat'} {Math.abs(cmp.activeUsers.change)}% vs prior period.</>
                  )}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalyticsPanel;